import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarCheck, Loader2 } from "lucide-react";
import { api, formatApiError, track } from "../lib/api";
import { EASE } from "./Reveal";

const GOALS = [
  "Higher Education Abroad",
  "Career Transition",
  "Visa & Admission Support",
  "Quranic Studies",
  "Job Interview Preparation",
  "Not Sure Yet",
];
const ROLES = ["Student", "Parent", "Working Professional"];
const STATUSES = ["High School", "Undergraduate", "Graduate", "Working Professional", "Other"];
const TIMELINES = ["Immediately", "1–3 months", "3–6 months", "6+ months"];
const COUNTRIES = ["USA", "UK", "Canada", "Australia", "Europe", "Middle East", "Not Sure Yet"];
const TIMES = ["Morning", "Afternoon", "Evening", "Weekend"];
const STEP_TITLES = ["Your Goal", "About You", "Timeline", "Contact"];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function OptionPill({ label, selected, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`rounded-full border px-4 py-2.5 text-left text-sm font-medium transition-[background-color,border-color,color,transform] duration-200 active:scale-[0.97] ${
        selected
          ? "border-forest bg-forest text-bone shadow-[0_6px_20px_rgba(19,78,58,0.3)]"
          : "border-border bg-white text-foreground hover:border-gold-dark/60"
      }`}
    >
      {label}
    </button>
  );
}

const inputClass =
  "h-12 w-full rounded-xl border border-input bg-white px-4 text-sm text-foreground outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

export function IntakeForm({ source = "unknown", idPrefix = "intake" }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    goal: "", role: "", status: "", timeline: "", country: "",
    preferred_time: "", name: "", email: "", phone: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    track("form_start", { form: "lead_intake", source });
  }, [source]);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setError("");
  };

  const validate = () => {
    if (step === 0 && !data.goal) return "Please choose your primary goal.";
    if (step === 1 && !data.role) return "Please tell us who this is for.";
    if (step === 2 && !data.timeline) return "Please pick a target timeline.";
    if (step === 3) {
      if (data.name.trim().length < 2) return "Please enter your full name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return "Please enter a valid email address.";
    }
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) return setError(err);
    track("intake_step_complete", { step: step + 1, source });
    setStep((s) => s + 1);
  };

  const submit = async () => {
    const err = validate();
    if (err) return setError(err);
    setSubmitting(true);
    try {
      const { status, ...rest } = data;
      await api.post("/leads", {
        ...rest,
        current_stage: status || null,
        phone: data.phone || null,
        area_of_interest: data.goal,
        source,
      });
      track("form_complete", { form: "lead_intake", source });
      track("booking_conversion", { source });
      setDone(true);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="py-6 text-center"
        data-testid={`${idPrefix}-success`}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 260, damping: 18 }}
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold shadow-[0_0_35px_rgba(212,175,55,0.45)]"
        >
          <CalendarCheck className="h-7 w-7 text-forest-deep" />
        </motion.div>
        <h3 className="mt-6 font-display text-2xl font-bold text-forest-deep">
          You're on the list{data.name ? `, ${data.name.split(" ")[0]}` : ""}.
        </h3>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
          A confirmation email is on its way to <span className="font-semibold text-forest">{data.email}</span>.
          One of our consultants will reach out within 24 hours to schedule your free strategy call.
        </p>
      </motion.div>
    );
  }

  return (
    <div data-testid={`${idPrefix}-form`}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">
          Step {step + 1} of 4 — {STEP_TITLES[step]}
        </p>
        <p className="font-mono text-[10px] text-muted-foreground">{Math.round(((step + 1) / 4) * 100)}%</p>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-secondary">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-forest to-gold"
          animate={{ width: `${((step + 1) / 4) * 100}%` }}
          transition={{ duration: 0.5, ease: EASE }}
        />
      </div>

      <div className="mt-7 min-h-[248px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 28 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -28 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            {step === 0 && (
              <div data-testid={`${idPrefix}-step-1`}>
                <p className="font-display text-lg font-bold text-forest-deep">What brings you here?</p>
                <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {GOALS.map((g) => (
                    <OptionPill key={g} label={g} selected={data.goal === g}
                      testId={`${idPrefix}-goal-${slugify(g)}`} onClick={() => set("goal", g)} />
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid={`${idPrefix}-step-2`} className="space-y-6">
                <div>
                  <p className="font-display text-lg font-bold text-forest-deep">Who is this for?</p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {ROLES.map((r) => (
                      <OptionPill key={r} label={r} selected={data.role === r}
                        testId={`${idPrefix}-role-${slugify(r)}`} onClick={() => set("role", r)} />
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground/70">Current stage <span className="font-normal text-muted-foreground">(optional)</span></p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {STATUSES.map((s) => (
                      <OptionPill key={s} label={s} selected={data.status === s}
                        testId={`${idPrefix}-status-${slugify(s)}`} onClick={() => set("status", data.status === s ? "" : s)} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid={`${idPrefix}-step-3`} className="space-y-6">
                <div>
                  <p className="font-display text-lg font-bold text-forest-deep">When do you want to move?</p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {TIMELINES.map((t) => (
                      <OptionPill key={t} label={t} selected={data.timeline === t}
                        testId={`${idPrefix}-timeline-${slugify(t)}`} onClick={() => set("timeline", t)} />
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground/70">Target destination <span className="font-normal text-muted-foreground">(optional)</span></p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {COUNTRIES.map((c) => (
                      <OptionPill key={c} label={c} selected={data.country === c}
                        testId={`${idPrefix}-country-${slugify(c)}`} onClick={() => set("country", data.country === c ? "" : c)} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid={`${idPrefix}-step-4`} className="space-y-4">
                <p className="font-display text-lg font-bold text-forest-deep">Where should we reach you?</p>
                <input data-testid={`${idPrefix}-name-input`} className={inputClass} placeholder="Full name"
                  value={data.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" />
                <input data-testid={`${idPrefix}-email-input`} className={inputClass} type="email" placeholder="Email address"
                  value={data.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
                <input data-testid={`${idPrefix}-phone-input`} className={inputClass} type="tel" placeholder="Phone / WhatsApp (optional)"
                  value={data.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                <div>
                  <p className="text-sm font-semibold text-foreground/70">Preferred call time <span className="font-normal text-muted-foreground">(optional)</span></p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {TIMES.map((t) => (
                      <OptionPill key={t} label={t} selected={data.preferred_time === t}
                        testId={`${idPrefix}-time-${slugify(t)}`} onClick={() => set("preferred_time", data.preferred_time === t ? "" : t)} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {error && (
        <p data-testid={`${idPrefix}-error`} className="mt-4 rounded-xl bg-destructive/10 px-4 py-2.5 text-sm font-medium text-destructive">
          {error}
        </p>
      )}

      <div className="mt-7 flex items-center justify-between gap-3">
        {step > 0 ? (
          <button type="button" data-testid={`${idPrefix}-back-btn`} onClick={() => setStep((s) => s - 1)}
            className="flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-foreground/60 transition-colors hover:text-forest">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : (
          <span />
        )}
        {step < 3 ? (
          <button type="button" data-testid={`${idPrefix}-continue-btn`} onClick={next}
            className="group flex items-center gap-2 rounded-full bg-forest px-6 py-3 font-display text-sm font-bold text-bone transition-[box-shadow,transform] duration-200 hover:shadow-[0_8px_25px_rgba(19,78,58,0.35)] active:scale-[0.97]">
            Continue <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        ) : (
          <button type="button" data-testid={`${idPrefix}-submit-btn`} onClick={submit} disabled={submitting}
            className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-display text-sm font-bold text-forest-deep transition-[box-shadow,transform] duration-200 hover:shadow-[0_8px_25px_rgba(212,175,55,0.45)] active:scale-[0.97] disabled:opacity-60">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Schedule My Free Call
          </button>
        )}
      </div>
    </div>
  );
}
